import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { useI18n } from "@/i18n";
import { useTenantUsage } from "@/hooks/useTenantUsage";
import { listWarehouses } from "@/infrastructure/warehouse/warehouseService";
import { listCategories } from "@/infrastructure/category/categoryService";
import {
  exportKardex, exportStock, exportBalance,
  getReportHistory, getReportDownloadUrl,
} from "@/infrastructure/reports/reportsService";
import type { ReportFormat, ReportHistoryItem, ReportHistoryMeta } from "@/infrastructure/reports/reportsService";
import type { WarehouseRecord, Category } from "@/domain/types";
import { Pagination } from "@/design-system";
import { colors, typography, transitions } from "@/design-system/tokens";
import { formatDate } from "@/utils/formatDate";
import { PlanLimitModal } from "../components/PlanLimitModal";

type ReportType = "kardex" | "stock" | "balance";
type ReportStatus = "ready" | "generating" | "failed";

const PAGE_SIZE = 3;

export const ReportsPage = memo(function ReportsPage() {
  const { t } = useI18n();
  const { isFeatureEnabled, maxReportsPerMonth } = useTenantUsage();
  const reportsEnabled = isFeatureEnabled("reports");
  const [items, setItems] = useState<ReportHistoryItem[]>([]);
  const [meta, setMeta] = useState<ReportHistoryMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [generating, setGenerating] = useState<ReportType | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<ReportType | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [planLimitMessage, setPlanLimitMessage] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReportHistory(page, PAGE_SIZE);
      setItems(res.items);
      setMeta(res.meta);
    } catch {}
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { listWarehouses().then(setWarehouses).catch(() => {}); }, []);
  useEffect(() => { listCategories({ limit: 100, status: "ACTIVE" }).then(res => setCategories(res.items)).catch(() => {}); }, []);

  const showToast = useCallback((msg: string, type: "success" | "error") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const filteredItems = items.filter(r =>
    r.reportName.toLowerCase().includes(search.toLowerCase()) ||
    r.reportType.toLowerCase().includes(search.toLowerCase())
  );

  const totalGenerated = meta.totalItems;
  const lastGeneratedDate = items.length > 0 ? formatDate(items[0]!.createdAt) : "—";

  // Count reports generated this month (client-side filter)
  const reportsThisMonth = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return items.filter(r => {
      const d = new Date(r.createdAt);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;
  }, [items]);

  const handleGenerate = useCallback((type: ReportType) => { setShowModal(type); }, []);

  const handleDownload = useCallback(async (reportId: string) => {
    setDownloadingId(reportId);
    try {
      const url = await getReportDownloadUrl(reportId);
      window.open(url, "_blank");
    } catch {
      showToast(t.reports.expired, "error");
    } finally {
      setTimeout(() => setDownloadingId(null), 1500);
    }
  }, [showToast, t.reports.expired]);

  const handleConfirmGenerate = useCallback(async (opts: { type: ReportType; warehouseId: string; productId: string; categoryId: string; from: string; to: string; format: ReportFormat }) => {
    setShowModal(null);
    setGenerating(opts.type);
    try {
      if (opts.type === "kardex") {
        await exportKardex({ format: opts.format, warehouseId: opts.warehouseId, productId: opts.productId || undefined, categoryId: opts.categoryId || undefined, from: opts.from || undefined, to: opts.to || undefined });
      } else if (opts.type === "stock") {
        await exportStock({ format: opts.format, warehouseId: opts.warehouseId, productId: opts.productId || undefined, categoryId: opts.categoryId || undefined });
      } else {
        await exportBalance({ format: opts.format, warehouseId: opts.warehouseId || undefined, productId: opts.productId || undefined, categoryId: opts.categoryId || undefined, from: opts.from || undefined, to: opts.to || undefined });
      }
      // Re-fetch history after successful generation
      await fetchHistory();
    } catch (err: any) {
      const status = err?.response?.status;
      // When responseType is blob, error response data is a Blob — parse it
      let msg = "";
      if (err?.response?.data instanceof Blob) {
        try { const text = await err.response.data.text(); const parsed = JSON.parse(text); msg = parsed.message ?? ""; } catch {}
      } else {
        msg = err?.response?.data?.message ?? "";
      }
      if (status === 403 && msg) {
        setPlanLimitMessage(msg);
      } else {
        showToast(msg || "Error", "error");
      }
    } finally {
      setGenerating(null);
    }
  }, [fetchHistory, showToast]);

  // Reports not included in plan
  if (!reportsEnabled) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "52px", height: "52px", background: "#f5f5f5", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="9" x2="15" y2="15" /><line x1="15" y1="9" x2="9" y2="15" /></svg>
        </div>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.analytics.reportsTitle}</h2>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: "8px 0 0" }}>{t.planLimit.tooltipDisabled}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      {/* Header */}
      <div>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.analytics.reportsTitle}</h1>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.analytics.reportsSubtitle}</p>
      </div>

      {/* Top section: Quick Generate + Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "16px", alignItems: "stretch" }}>
        {/* Quick Generate */}
        <div style={{ border: "1px solid #eee", borderRadius: "14px", padding: "24px" }}>
          <h3 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: "0 0 16px" }}>{t.reports.quickGenerate}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <QuickCard icon={<KardexIcon />} title={t.reports.kardexTitle} desc={t.reports.kardexDesc} onGenerate={() => handleGenerate("kardex")} loading={generating === "kardex"} t={t} />
            <QuickCard icon={<StockIcon />} title={t.reports.stockTitle} desc={t.reports.stockDesc} onGenerate={() => handleGenerate("stock")} loading={generating === "stock"} t={t} />
            <QuickCard icon={<InventoryIcon />} title={t.reports.balanceTitle} desc={t.reports.balanceDesc} onGenerate={() => handleGenerate("balance")} loading={generating === "balance"} t={t} />
          </div>
        </div>
        {/* Total stat */}
        <div style={{ border: "1px solid #eee", borderRadius: "14px", padding: "24px", minWidth: "160px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "6px" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888", fontWeight: typography.weight.medium }}>{t.reports.totalGenerated}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "2rem", fontWeight: typography.weight.bold, color: "#111" }}>{totalGenerated}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>{t.reports.totalGeneratedDesc}</span>
        </div>
        {/* Last Generated stat */}
        <div style={{ border: "1px solid #eee", borderRadius: "14px", padding: "24px", minWidth: "180px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "6px" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888", fontWeight: typography.weight.medium }}>{t.reports.lastGenerated}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "1.3rem", fontWeight: typography.weight.bold, color: "#111" }}>{lastGeneratedDate}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>{t.reports.lastGeneratedDesc}</span>
        </div>
      </div>

      {/* Generated Reports table */}
      <div style={{ border: "1px solid #eee", borderRadius: "14px", padding: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <h3 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.reports.generatedReports}</h3>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f9f9f9", border: "1px solid #eee", borderRadius: "9px", padding: "8px 14px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.reports.filterPlaceholder} style={{ border: "none", outline: "none", background: "transparent", fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#333", width: "140px" }} />
            </div>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontFamily: typography.fontFamily, fontSize: typography.size.sm }}>...</div>
        ) : filteredItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontFamily: typography.fontFamily, fontSize: typography.size.sm }}>{t.reports.noReports}</div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.reports.colName, t.reports.colType, t.reports.colDateRange, t.reports.colFormat, t.reports.colGeneratedOn, t.reports.colStatus, ""].map((col, i) => (
                    <th key={i} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "12px 14px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(report => (
                  <tr key={report.id} style={{ borderBottom: "1px solid #f7f7f7" }}>
                    <td style={cellStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{report.reportName}</span></td>
                    <td style={cellStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555", textTransform: "capitalize" }}>{report.reportType}</span></td>
                    <td style={cellStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{report.dateFrom && report.dateTo ? `${report.dateFrom} – ${report.dateTo}` : "—"}</span></td>
                    <td style={cellStyle}>
                      <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: report.format === "pdf" ? "#dc2626" : "#16a34a", background: report.format === "pdf" ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)", padding: "3px 8px", borderRadius: "5px", textTransform: "uppercase" }}>{report.format}</span>
                    </td>
                    <td style={cellStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{formatDate(report.createdAt)}</span></td>
                    <td style={cellStyle}><StatusBadge status={report.status as ReportStatus} t={t} /></td>
                    <td style={cellStyle}>
                      {report.status === "ready" && (
                        <button
                          onClick={() => handleDownload(report.id)}
                          disabled={downloadingId === report.id}
                          style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#fff", background: downloadingId === report.id ? "#999" : colors.primary, border: "none", borderRadius: "7px", padding: "6px 14px", cursor: downloadingId === report.id ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }}
                          onMouseEnter={e => { if (downloadingId !== report.id) (e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover; }}
                          onMouseLeave={e => { if (downloadingId !== report.id) (e.currentTarget as HTMLButtonElement).style.background = colors.primary; }}
                        >
                          {downloadingId === report.id ? <MiniSpinner /> : <DownloadIcon />}
                          {downloadingId === report.id ? "..." : t.reports.download}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ marginTop: "16px", display: "flex", justifyContent: "center" }}>
                <Pagination currentPage={page} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Generate modal */}
      {showModal && <GenerateModal type={showModal} warehouses={warehouses} categories={categories} onConfirm={handleConfirmGenerate} onClose={() => setShowModal(null)} t={t} />}
      {/* Toast */}
      {toast && <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: toast.type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300 }}>{toast.message}</div>}
      {planLimitMessage && <PlanLimitModal message={planLimitMessage} onClose={() => setPlanLimitMessage(null)} onViewPlan={() => setPlanLimitMessage(null)} />}
    </div>
  );
});

/* --- Sub-components --- */

const cellStyle: React.CSSProperties = { padding: "14px 14px", verticalAlign: "middle" };

function QuickCard({ icon, title, desc, onGenerate, loading, t }: { icon: React.ReactNode; title: string; desc: string; onGenerate: () => void; loading: boolean; t: any }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "16px", background: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ color: "#555" }}>{icon}</div>
        <div>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111", display: "block" }}>{title}</span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{desc}</span>
        </div>
      </div>
      <button onClick={onGenerate} disabled={loading} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#fff", background: loading ? "#999" : "#111", border: "none", borderRadius: "8px", padding: "9px 0", cursor: loading ? "not-allowed" : "pointer", transition: `background ${transitions.fast}`, width: "100%" }} onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }} onMouseLeave={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}>
        {loading ? "..." : t.reports.generate}
      </button>
    </div>
  );
}

function StatusBadge({ status, t }: { status: ReportStatus; t: any }) {
  const config: Record<ReportStatus, { bg: string; color: string; label: string }> = {
    ready: { bg: "rgba(34,197,94,0.1)", color: "#16a34a", label: t.reports.statusReady },
    generating: { bg: "rgba(245,158,11,0.1)", color: "#d97706", label: t.reports.statusGenerating },
    failed: { bg: "rgba(239,68,68,0.1)", color: "#dc2626", label: t.reports.statusFailed },
  };
  const cfg = config[status] ?? config.ready;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: cfg.bg, color: cfg.color, fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}>
      <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function GenerateModal({ type, warehouses, categories, onConfirm, onClose, t }: { type: ReportType; warehouses: WarehouseRecord[]; categories: Category[]; onConfirm: (opts: { type: ReportType; warehouseId: string; productId: string; categoryId: string; from: string; to: string; format: ReportFormat }) => void; onClose: () => void; t: any }) {
  const [warehouseId, setWarehouseId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [format, setFormat] = useState<ReportFormat>("xlsx");

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const title = type === "kardex" ? t.reports.kardexTitle : type === "stock" ? t.reports.stockTitle : t.reports.balanceTitle;
  const warehouseRequired = false; // All 3 reports now have optional warehouse
  const isValid = warehouseRequired ? !!warehouseId : true;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    onConfirm({ type, warehouseId, productId: "", categoryId, from, to, format });
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "440px", display: "flex", flexDirection: "column", gap: "18px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.reports.generate} — {title}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {/* Warehouse */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.reports.warehouseLabel}{warehouseRequired ? " *" : ""}</label>
            <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)} style={selectStyle}>
              <option value="">{warehouseRequired ? t.reports.warehousePlaceholder : t.reports.allWarehouses}</option>
              {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</option>)}
            </select>
          </div>

          {/* Category */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.products.categoryLabel}</label>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={selectStyle}>
              <option value="">{t.products.filterAllCategories}</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Date range with presets */}
          {(type === "kardex" || type === "stock") && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <label style={labelStyle}>{t.reports.fromLabel} / {t.reports.toLabel}</label>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {DATE_PRESETS.map(preset => (
                  <button key={preset.key} type="button" onClick={() => { setFrom(preset.getFrom()); setTo(toISO(new Date())); }}
                    style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: "#555", background: "#f5f5f5", border: "1px solid #e8e8e8", borderRadius: "7px", padding: "5px 10px", cursor: "pointer", transition: `all ${transitions.fast}` }}
                    onMouseEnter={e => { const el = e.currentTarget; el.style.background = "#111"; el.style.color = "#fff"; el.style.borderColor = "#111"; }}
                    onMouseLeave={e => { const el = e.currentTarget; el.style.background = "#f5f5f5"; el.style.color = "#555"; el.style.borderColor = "#e8e8e8"; }}
                  >{(t.reports as any)[preset.key]}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              </div>
            </div>
          )}
          {type === "balance" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <label style={labelStyle}>{t.reports.fromLabel} / {t.reports.toLabel}</label>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {MONTH_PRESETS.map(preset => (
                  <button key={preset.key} type="button" onClick={() => { setFrom(preset.getFrom()); setTo(toMonth(new Date())); }}
                    style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: "#555", background: "#f5f5f5", border: "1px solid #e8e8e8", borderRadius: "7px", padding: "5px 10px", cursor: "pointer", transition: `all ${transitions.fast}` }}
                    onMouseEnter={e => { const el = e.currentTarget; el.style.background = "#111"; el.style.color = "#fff"; el.style.borderColor = "#111"; }}
                    onMouseLeave={e => { const el = e.currentTarget; el.style.background = "#f5f5f5"; el.style.color = "#555"; el.style.borderColor = "#e8e8e8"; }}
                  >{(t.reports as any)[preset.key]}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <input type="month" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input type="month" value={to} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              </div>
            </div>
          )}

          {/* Format */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.reports.formatLabel}</label>
            <div style={{ display: "flex", gap: "10px" }}>
              <FormatToggle label="Excel" selected={format === "xlsx"} onClick={() => setFormat("xlsx")} />
              <FormatToggle label="PDF" selected={format === "pdf"} onClick={() => setFormat("pdf")} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "6px" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.reports.cancel}</button>
            <button type="submit" disabled={!isValid} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isValid ? "#111" : "#ccc", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isValid ? "pointer" : "not-allowed", transition: `background ${transitions.fast}` }} onMouseEnter={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }} onMouseLeave={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}>{t.reports.generate}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormatToggle({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: selected ? "#111" : "#999", background: selected ? "#f0f0f0" : "transparent", border: `1.5px solid ${selected ? "#111" : "#e5e5e5"}`, borderRadius: "8px", padding: "8px 18px", cursor: "pointer", transition: `all ${transitions.fast}` }}>
      {label}
    </button>
  );
}

/* --- Styles --- */
const labelStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" };
const selectStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 14px", outline: "none", width: "100%", boxSizing: "border-box", cursor: "pointer" };
const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 14px", outline: "none", boxSizing: "border-box" };

/* --- Date presets --- */
function toISO(date: Date): string { return date.toISOString().slice(0, 10); }
function toMonth(date: Date): string { return date.toISOString().slice(0, 7); }
function daysAgo(days: number): string { const d = new Date(); d.setDate(d.getDate() - days); return toISO(d); }
function monthsAgo(months: number): string { const d = new Date(); d.setMonth(d.getMonth() - months); return toISO(d); }
function monthsAgoMonth(months: number): string { const d = new Date(); d.setMonth(d.getMonth() - months); return toMonth(d); }

const DATE_PRESETS: { key: string; getFrom: () => string }[] = [
  { key: "preset15Days", getFrom: () => daysAgo(15) },
  { key: "preset1Month", getFrom: () => monthsAgo(1) },
  { key: "preset2Months", getFrom: () => monthsAgo(2) },
  { key: "preset3Months", getFrom: () => monthsAgo(3) },
  { key: "preset5Months", getFrom: () => monthsAgo(5) },
  { key: "preset6Months", getFrom: () => monthsAgo(6) },
  { key: "preset1Year", getFrom: () => monthsAgo(12) },
];

const MONTH_PRESETS: { key: string; getFrom: () => string }[] = [
  { key: "preset1Month", getFrom: () => monthsAgoMonth(1) },
  { key: "preset2Months", getFrom: () => monthsAgoMonth(2) },
  { key: "preset3Months", getFrom: () => monthsAgoMonth(3) },
  { key: "preset5Months", getFrom: () => monthsAgoMonth(5) },
  { key: "preset6Months", getFrom: () => monthsAgoMonth(6) },
  { key: "preset1Year", getFrom: () => monthsAgoMonth(12) },
];

/* --- Icons --- */
function KardexIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>;
}
function StockIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
}
function InventoryIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>;
}
function DownloadIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
}
function MiniSpinner() {
  return <span style={{ width: "12px", height: "12px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />;
}
